import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

const faqs = [
  {
    category: 'Orders & Shipping',
    questions: [
      {
        q: 'How long does shipping take?',
        a: 'Domestic orders (India) are delivered within 5-7 business days. International orders typically take 10-14 business days depending on the destination country and customs processing.'
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes! We ship to over 50 countries worldwide. Shipping rates and delivery times vary by destination. You can see the exact shipping cost at checkout.'
      },
      {
        q: 'How can I track my order?',
        a: 'Once your order ships, you\'ll receive a tracking number via email. You can also track your order anytime using our Track Order page with your order number and email.'
      },
      {
        q: 'What are the shipping charges?',
        a: 'We offer free shipping on orders above ₹5,000 within India. For international orders, shipping is calculated based on destination and package weight.'
      }
    ]
  },
  {
    category: 'Products & Care',
    questions: [
      {
        q: 'Are all products handmade?',
        a: 'Yes, every Chaleur piece is handcrafted by skilled artisans using traditional techniques passed down through generations. Each piece is unique and may have slight variations that are a mark of authenticity.'
      },
      {
        q: 'How do I care for my scarf?',
        a: 'We recommend dry cleaning or gentle hand washing in cold water with mild detergent. Lay flat to dry away from direct sunlight. Store folded in a cool, dry place. Detailed care instructions are included with each product.'
      },
      {
        q: 'What materials do you use?',
        a: 'We use premium natural fibers including silk, cashmere, merino wool, and organic cotton. All materials are ethically sourced and many are certified sustainable.'
      }
    ]
  },
  {
    category: 'Returns & Refunds',
    questions: [
      {
        q: 'What is your return policy?',
        a: 'We accept returns within 14 days of delivery for unused items in original packaging. Please visit our Refund Policy page for complete details.'
      },
      {
        q: 'How do I initiate a return?',
        a: 'Contact us at sabinakamal@gmail.com with your order number and reason for return. We\'ll provide you with return instructions and a prepaid shipping label for domestic orders.'
      },
      {
        q: 'When will I receive my refund?',
        a: 'Refunds are processed within 5-7 business days after we receive and inspect the returned item. The amount will be credited to your original payment method.'
      }
    ]
  },
  {
    category: 'Gifting',
    questions: [
      {
        q: 'Do you offer gift wrapping?',
        a: 'Yes! Our luxury gift wrapping includes handcrafted packaging with artisan-made paper, silk ribbons, and a hand-calligraphed message card. Select this option at checkout.'
      },
      {
        q: 'Can I ship directly to the recipient?',
        a: 'Absolutely. Enter the recipient\'s address at checkout and select "This is a gift." We\'ll include a gift receipt without prices and your personal message.'
      },
      {
        q: 'Do you offer corporate gifting?',
        a: 'Yes, we offer special rates for bulk orders of 20+ pieces. Contact us at sabinakamal@gmail.com for custom packaging options, dedicated account management, and volume pricing.'
      }
    ]
  }
];

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIdx, questionIdx) => {
    const key = `${categoryIdx}-${questionIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="pt-32 pb-24" data-testid="faq-page">
      <div className="chaleur-container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="caption text-primary mb-4">Help Center</p>
          <h1 className="text-4xl md:text-5xl font-light mb-6">Frequently Asked Questions</h1>
          <p className="text-muted-foreground">
            Find answers to common questions about orders, shipping, products, and more.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-12">
          {faqs.map((category, catIdx) => (
            <div key={catIdx}>
              <h2 className="text-xl font-medium mb-6 pb-2 border-b border-border">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((item, qIdx) => {
                  const key = `${catIdx}-${qIdx}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={qIdx} className="border border-border">
                      <button
                        onClick={() => toggleItem(catIdx, qIdx)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors"
                      >
                        <span className="font-medium pr-4">{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 flex-shrink-0 text-primary" />
                        ) : (
                          <ChevronDown className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 text-muted-foreground">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto mt-16 text-center p-8 bg-secondary/30 border border-border">
          <h3 className="text-xl font-medium mb-4">Still have questions?</h3>
          <p className="text-muted-foreground mb-6">
            Can't find what you're looking for? We're here to help.
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center justify-center h-12 px-8 bg-primary text-primary-foreground uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
